import { Trans, useTranslation } from "react-i18next";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Button, Icon, Skeleton, Spinner } from "@thetreep/ui";
import { SolutionItem } from "./Solution";
import { ConnectError } from "@connectrpc/connect";
import { useToaster } from "@hooks/useToaster";

import OfferItem from "./Offer/OfferItem";
import { FiltersResults } from "./FiltersResults/FiltersResults";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import { SearchExtension, Solution } from "@gen/travel/v1/search_pb";
import i18n from "@libs/i18n";
import { routes } from "@routes";
import { useDAP } from "../dap.hook";
import {
  isFilteredSolution,
  isFilteredOffer,
  sortSolutions,
} from "./FiltersResults/utils";
import { useBookingStore } from "@stores/useBookingStore";
import { SearchLoader } from "@pages/booking/search/SearchLoader";
import {
  getPlacementChoices,
  isTrain,
  SearchDate,
  searchJourney,
  toInwardRequest,
  withTravelerIds,
} from "@services/travel";
import { useLoadingEffect } from "@hooks/useLoadingEffect";
import { SelectToggle } from "@components/common/SelectToggle";
import { CabinClass } from "../search/Search";
import { SearchResponse } from "@services/travel";
import { BannerMessage } from "@components/common/BannerMessage";
import {
  Offer,
  TrainCabinClass,
  Transportation,
} from "@gen/travel/v1/common_pb";
import { ExchangeOffer, ExchangeSolution } from "@gen/travel/v1/exchange_pb";
import { localDateTimeToDate } from "@utils/date";
import { getAgencyInfo } from "@services/caisse";
import { AgencyInfo } from "@gen/agency/v1/agency_pb";
import { OfflineModal } from "@pages/user/bookings/OfflineModal";
import {
  PlaneCabinClass as DAPPlaneCabinClass,
  TrainCabinClass as DAPTrainCabinClass,
} from "@gen/dap/v1/dap_pb";

type FiltersState = {
  directJourney: boolean;
  flexibility: string[];
  sortBy: string;
  priceRange: number[];
};

export function Results() {
  const { t } = useTranslation();
  const location = useLocation();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { addToast } = useToaster();
  const { dap } = useDAP();
  const [selectedOffer, setSelectedOffer] = useState<{
    offer: Offer | ExchangeOffer;
    solution: Solution | ExchangeSolution;
  } | null>(null);
  const [results, setResults] = useState<SearchResponse>();
  const [isExchange, setIsExchange] = useState<boolean>(false);
  const [filtersMobileOpened, setFiltersMobileOpened] =
    useState<boolean>(false);
  const [filters, setFilters] = useState<FiltersState>({
    directJourney: false,
    flexibility: [],
    sortBy: "price",
    priceRange: [0, 0],
  });

  const [isOfflineModalOpen, setOfflineModalOpen] = useState(false);

  const {
    searchRequest,
    outwardOffer,
    inwardOffer,
    outwardResults,
    setOutwardResults,
    setOutwardOffer,
    setInwardOffer,
    setInwardResults,
    setSearchRequest,
    setOutwardPlacementChoices,
    setInwardPlacementChoices,
  } = useBookingStore();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPreviousLoading, setIsPreviousLoading] = useState<boolean>(false);
  const [isNextLoading, setIsNextLoading] = useState<boolean>(false);
  const [agencyInfos, setAgencyInfos] = useState<AgencyInfo>();
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);

  useLoadingEffect(isLoading);
  const [selectedTransport, setSelectedTransport] = useState<Transportation>(
    hasTransportation("train") ? Transportation.TRAIN : Transportation.PLANE,
  );
  const [listVisible, setListVisible] = useState<boolean>(true);
  const [selectedOfferClass, setSelectedOfferClass] = useState<CabinClass>(
    TrainCabinClass.SECOND,
  );
  const navigationType = useNavigationType();

  function hasTransportation(type: "train" | "plane") {
    if (type === "train") {
      return dap?.transportations?.includes(Transportation.TRAIN);
    }
    return dap?.transportations?.includes(Transportation.PLANE);
  }

  // lock the transporation selection to what was selected for the outward search.
  function transportations() {
    const train = {
      value: Transportation.TRAIN,
      label: t("train"),
      icon: "train_4_line",
    };
    const plane = {
      value: Transportation.PLANE,
      label: t("plane"),
      icon: "airplane_line",
    };
    const isTrainSelected = selectedOffer && isTrain(selectedOffer?.solution);
    const canIncludeTrain = !outwardOffer || isTrainSelected;
    const canIncludePlane =
      !outwardOffer || (selectedOffer && !isTrain(selectedOffer?.solution));

    return {
      key: "transportation",
      options: [
        ...(canIncludeTrain && hasTransportation("train") ? [train] : []),
        ...(canIncludePlane && hasTransportation("plane") ? [plane] : []),
      ],
    };
  }

  useEffect(() => {
    const searchRequest = location.state?.searchRequest;
    const searchResponse = location.state?.searchResponse;

    //if navigating with back browser button
    if (navigationType === "POP") {
      if (outwardOffer && !inwardOffer) {
        setOutwardOffer(null);
      } else if (outwardOffer && inwardOffer) {
        setInwardOffer(null);
      }
    }

    if (!outwardOffer) {
      if (!searchResponse) {
        navigate(routes.SEARCH);
      } else if (searchResponse !== outwardResults) {
        // the store is lost on reload but history state is not
        setOutwardResults(searchResponse);
      }
    }

    setSearchRequest(searchRequest);
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setResults(searchResponse);
    setIsExchange(location.state?.exchange);
  }, [location]);

  const onSolutionSelect = (
    solution: Solution | ExchangeSolution,
    cabinClass: CabinClass,
  ) => {
    const offer = solution.offers
      .filter((o) => o.cabinClass.value && o.cabinClass.value === cabinClass)
      .at(0);

    if (!offer) {
      return;
    }
    setListVisible(false);
    setSelectedOffer({
      solution,
      offer,
    });

    if (offer.cabinClass.value) {
      setSelectedOfferClass(offer.cabinClass.value);
    }
  };
  const onClassSelect = (cabinClass: CabinClass) => {
    setSelectedOfferClass(cabinClass);
  };

  const handleConfirmChoice = async (offer: Offer | ExchangeOffer) => {
    if (!offer.isCompliant) {
      await fetchAgencyInfos();
      setOfflineModalOpen(true);
      return;
    }

    setIsConfirmLoading(true);
    const solution = selectedOffer?.solution;
    if (!outwardOffer && searchRequest && solution) {
      try {
        const placementChoices = await getPlacementChoices(
          results?.travelId ?? "",
          dap.number,
          [selectedOffer?.offer.id],
        );

        if (placementChoices && placementChoices.placements.length > 0) {
          setOutwardOffer({ offer, solution });
          setOutwardPlacementChoices(placementChoices);
          navigate(routes.PLACEMENTS + search, {
            state: { searchRequest },
          });
          setIsConfirmLoading(false);
          return;
        }
      } catch (e) {
        if (e instanceof ConnectError && e.rawMessage) {
          addToast("error", e.rawMessage);
        } else {
          console.error(e);
          addToast("error", t("placement.searchError"));
        }
      }

      if (offer.ancillaries.length > 0) {
        setOutwardOffer({ offer, solution });
        navigate(routes.OPTIONS + search, {
          state: { searchRequest, type: "outward" },
        });
      } else {
        try {
          setIsLoading(true);
          const response = await searchJourney(
            toInwardRequest(
              searchRequest,
              dap,
              offer.id,
              outwardResults?.travelId,
            ),
          );
          if (!response) throw new Error("null response");
          setSelectedOffer(null);
          setListVisible(true);
          setResults(response);
          setInwardResults(response);
          setOutwardOffer({ offer, solution });
        } catch (e) {
          if (e instanceof ConnectError) {
            addToast("error", e.rawMessage);
          }
        } finally {
          setIsLoading(false);
        }
      }
    }

    if (
      (outwardOffer && searchRequest && solution) ||
      (isExchange && solution)
    ) {
      setInwardOffer({ offer, solution });

      try {
        const placementChoices = await getPlacementChoices(
          results?.travelId ?? "",
          dap.number,
          [selectedOffer?.offer.id],
        );

        if (placementChoices && placementChoices.placements.length > 0) {
          setInwardPlacementChoices(placementChoices);
          navigate(routes.PLACEMENTS + search, {
            state: { searchRequest },
          });
          setIsConfirmLoading(false);
          return;
        } else if (offer.ancillaries.length > 0) {
          navigate(routes.OPTIONS + search, {
            state: { searchRequest, type: "outward" },
          });
          // inward
        } else if (
          outwardOffer?.offer.isPassportRequired ||
          inwardOffer?.offer.isPassportRequired
        ) {
          navigate(routes.PASSPORT + search);
        } else {
          navigate(routes.RECAP + search);
        }
      } catch (e) {
        if (e instanceof ConnectError && e.rawMessage) {
          addToast("error", t("placement.searchError"));
        } else {
          console.error(e);
          addToast("error", t("placement.searchError"));
        }
      }
    }
    setIsConfirmLoading(false);
  };

  const solutionsTransports = useMemo(() => {
    return results?.solutions.flatMap((solution) =>
      solution.journey?.segments.map((s) => s.transport?.type),
    );
  }, [results]);

  // Auto select the transport when only one is available in the results
  const [prevTransports, setPrevTransports] = useState(solutionsTransports);
  if (solutionsTransports !== prevTransports) {
    setPrevTransports(solutionsTransports);
    if (solutionsTransports?.length === 1 && solutionsTransports[0]) {
      setSelectedTransport(solutionsTransports[0]);
    }
  }

  const solutions = useMemo(() => {
    if (!results) {
      return [];
    }
    const solutions = results.solutions
      // keep only selected transport
      .filter((solution) =>
        solution.journey?.segments.every(
          (s) => s.transport?.type == selectedTransport,
        ),
      )
      // keep only solutions with offers that match the filters
      .filter(
        (solution) =>
          solution.offers.length > 0 &&
          solution.offers.some((o) => isFilteredOffer(o, filters)),
      );

    if (solutions.length === 0) {
      fetchAgencyInfos();
    }
    return sortSolutions(solutions, filters);
  }, [results, filters, selectedTransport]);

  async function searchAdditionalSolutions(
    date: SearchDate,
    extension: SearchExtension,
  ): Promise<Solution[]> {
    if (!searchRequest || !results) return [];
    // searchRequest is always the outward one, the leg comes from the selected outward offer
    const leg = outwardOffer
      ? {
          ...toInwardRequest(searchRequest, dap, outwardOffer.offer.id),
          inwardDate: date,
        }
      : { ...searchRequest, outwardDate: date };
    try {
      const res = await searchJourney({
        ...withTravelerIds(leg, results.passengers),
        travelId: results.travelId,
        extension,
        transportations: [selectedTransport],
      });
      return res?.solutions ?? [];
    } catch (e) {
      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
      return [];
    }
  }

  const hasSolutions = solutions && solutions.length > 0;

  // the store is kept in sync so going back from the recap shows every loaded page
  function appendSolutions(more: Solution[]) {
    if (!results) return;
    const merged = { ...results, solutions: [...results.solutions, ...more] };
    setResults(merged);
    if (outwardOffer) {
      setInwardResults(merged);
    } else {
      setOutwardResults(merged);
    }
  }

  function scheduleTime(
    s: Solution | ExchangeSolution,
    key: "departure" | "arrival",
  ) {
    const d = s.journey?.schedule?.[key];
    return d ? localDateTimeToDate(d).getTime() : undefined;
  }

  // next pages from the latest departure, previous from the earliest arrival
  async function loadMore(direction: "next" | "previous") {
    const { key, edge, extension, beyond, setLoading } =
      direction === "next"
        ? ({
            key: "departure",
            edge: Math.max,
            extension: SearchExtension.NEXT,
            beyond: (t: number, bound: number) => t > bound,
            setLoading: setIsNextLoading,
          } as const)
        : ({
            key: "arrival",
            edge: Math.min,
            extension: SearchExtension.PREVIOUS,
            beyond: (t: number, bound: number) => t < bound,
            setLoading: setIsPreviousLoading,
          } as const);

    const times = solutions.flatMap((s) => scheduleTime(s, key) ?? []);
    if (times.length === 0) return;
    const bound = edge(...times);

    setLoading(true);
    try {
      const res = await searchAdditionalSolutions(
        { date: new Date(bound), type: key },
        extension,
      );
      appendSolutions(
        res.filter((s) => {
          const t = scheduleTime(s, key);
          return t !== undefined && beyond(t, bound);
        }),
      );
    } finally {
      setLoading(false);
    }
  }

  function printDate(index: number) {
    if (index === 0) {
      return (
        <span className="font-jakarta text-gray-900">
          {departureDate(solutions[0])}
        </span>
      );
    }

    const previousSolutionDeparture =
      solutions[index - 1].journey?.schedule?.departure;
    const currentSolutionDeparture =
      solutions[index].journey?.schedule?.departure;

    if (!previousSolutionDeparture || !currentSolutionDeparture) return;

    const previousDepartureDate = localDateTimeToDate(
      previousSolutionDeparture,
    );
    const currentDepartureDate = localDateTimeToDate(currentSolutionDeparture);

    if (!sameDay(previousDepartureDate, currentDepartureDate)) {
      return (
        <span className="font-jakarta text-gray-900">
          {departureDate(solutions[index])}
        </span>
      );
    }
  }

  function sameDay(a: Date, b: Date) {
    return (
      a.getFullYear() == b.getFullYear() &&
      a.getMonth() == b.getMonth() &&
      a.getDate() == b.getDate()
    );
  }

  function departureDate(solution: Solution | ExchangeSolution): string {
    const departure = solution.journey?.schedule?.departure;
    if (!departure) {
      return "";
    }
    return Intl.DateTimeFormat(i18n.language, {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(localDateTimeToDate(departure));
  }

  async function fetchAgencyInfos() {
    if (!dap?.caisseId) {
      console.error("could not get agency infos");
      return;
    }
    const agencyInfos = await getAgencyInfo(dap.caisseId);
    if (!agencyInfos.result) {
      console.error("could not get agency infos");
      return;
    }
    switch (agencyInfos.result.case) {
      case "agencyInfo":
        setAgencyInfos(agencyInfos.result.value);
        break;
      case "error":
        console.error("could not get agency infos");
        break;
    }
    return agencyInfos.result;
  }

  if (!results || !dap) {
    return;
  }

  const dapIncludesTrainPremier =
    !dap.trainCabinClass ||
    [DAPTrainCabinClass.PREMIER, DAPTrainCabinClass.UNSPECIFIED].includes(
      dap.trainCabinClass,
    );
  const dapIncludesPlaneBusiness =
    !dap.planeCabinClass ||
    [DAPPlaneCabinClass.BUSINESS, DAPPlaneCabinClass.UNSPECIFIED].includes(
      dap.planeCabinClass,
    );
  const dapIncludesPlaneEconomyPremium =
    !dap.planeCabinClass ||
    [
      DAPPlaneCabinClass.ECONOMY_PREMIUM,
      DAPPlaneCabinClass.BUSINESS,
      DAPPlaneCabinClass.UNSPECIFIED,
    ].includes(dap.planeCabinClass);

  return (
    <>
      <div className="flex flex-1 min-w-0 flex-col gap-6">
        {isLoading && <SearchLoader type={"return"} />}

        <h1
          data-testid="journey-header"
          className="text-xl md:text-[32px] font-jakarta font-bold md:mb-6 break-words"
        >
          {!outwardOffer
            ? `${t("search.outward")}: ${dap.origin?.name} - ${
                dap.destination?.name
              }`
            : `${t("search.return")}: ${dap.destination?.name} - ${
                dap.origin?.name
              }`}
        </h1>
        <SelectToggle
          field={transportations()}
          value={selectedTransport}
          onChange={(value) => {
            setSelectedTransport(value);
            setSelectedOffer(null);
          }}
        />

        <div className="flex flex-col gap-6">
          <div className="hidden lg:block">
            <FiltersResults
              isMobileOpened={filtersMobileOpened}
              onFilterChange={setFilters}
              onClose={() => setFiltersMobileOpened(false)}
            />
          </div>
          {!listVisible ? (
            <div className="block lg:hidden">
              <Button
                size="mini"
                width="100%"
                kind="outline"
                startEnhancer={<Icon src="arrow_left_line" />}
                onClick={() => setListVisible(true)}
              >
                {t("results.returnList")}
              </Button>
            </div>
          ) : (
            <div className="block lg:hidden">
              <Button
                size="mini"
                width="100%"
                kind="outline"
                startEnhancer={<Icon color="#0C419A" src="settings_6_line" />}
                onClick={() => setFiltersMobileOpened(true)}
              >
                {t("results.sortAndFilter")}
              </Button>
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <div>
            {hasSolutions && (
              <button
                type="button"
                disabled={isPreviousLoading}
                className="flex items-center gap-2 text-cnam-blue text-sm md:text-base font-bold font-jakarta mt-4 mb-6
                        "
                onClick={() => loadMore("previous")}
              >
                {isPreviousLoading ? (
                  <Spinner $size={12} />
                ) : (
                  <Icon src="arrow_up_line" />
                )}
                {selectedTransport == Transportation.TRAIN
                  ? t("results.previousTrains")
                  : t("results.previousFlights")}
              </button>
            )}
            <div className="flex flex-1 gap-6">
              <div
                className={`flex-1 ${listVisible ? "block" : "hidden"} lg:block`}
              >
                <div className="flex justify-between">
                  <div></div>
                  <div
                    className={`space-x-4 pr-4 hidden whitespace-nowrap h-0 ${
                      selectedTransport == Transportation.TRAIN
                        ? "xl:flex"
                        : "2xl:flex"
                    } space-x-4`}
                  >
                    {selectedTransport == Transportation.TRAIN ? (
                      <>
                        <div className="w-[96px] font-jakarta text-center">
                          {t("results.secondClass")}
                        </div>

                        {dapIncludesTrainPremier && (
                          <div className="w-[96px]  text-center">
                            {t("results.firstClass")}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="w-[96px] font-jakarta text-center">
                          {t("results.economy")}
                        </div>

                        {dapIncludesPlaneEconomyPremium && (
                          <div className="w-[96px] font-jakarta text-center">
                            {t("results.economyPremium")}
                          </div>
                        )}

                        {dapIncludesPlaneBusiness && (
                          <div className="w-[96px] font-jakarta text-center">
                            {t("results.business")}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-4 min-h-[300px]">
                  {results.warnings?.map((w, i) => (
                    <BannerMessage key={i}>
                      <div dangerouslySetInnerHTML={{ __html: w }} />
                    </BannerMessage>
                  ))}
                  {!hasSolutions ? (
                    <BannerMessage>
                      <Trans
                        t={t}
                        values={{
                          transport:
                            selectedTransport == Transportation.TRAIN
                              ? t("train").toLowerCase()
                              : t("plane").toLowerCase(),
                        }}
                      >
                        results.noSolutions
                      </Trans>
                      <br />
                      {!agencyInfos ? (
                        <Skeleton animation height="100px" />
                      ) : (
                        <Trans
                          t={t}
                          values={{
                            phone: agencyInfos?.contactPhone,
                            openHours: agencyInfos?.openingHours,
                          }}
                        >
                          results.agencyInfos
                        </Trans>
                      )}
                    </BannerMessage>
                  ) : (
                    solutions.map(
                      (
                        solution: Solution | ExchangeSolution,
                        index: number,
                      ) => (
                        <Fragment key={index}>
                          {printDate(index)}
                          <SolutionItem
                            dap={dap}
                            solution={solution}
                            index={index}
                            selectedTransport={selectedTransport}
                            selectedClass={
                              selectedOffer?.solution.journey?.id ==
                              solution.journey?.id
                                ? selectedOfferClass
                                : undefined
                            }
                            isFilteredSolution={(solution, classType) =>
                              isFilteredSolution(solution, filters, classType)
                            }
                            isFilteredOffer={(offer) =>
                              isFilteredOffer(offer, filters)
                            }
                            onSolutionSelect={onSolutionSelect}
                          />
                        </Fragment>
                      ),
                    )
                  )}
                </div>
              </div>
              {hasSolutions &&
                selectedOffer &&
                selectedOffer.solution.journey && (
                  <OfferItem
                    dap={dap}
                    inward={!!outwardOffer}
                    transport={selectedTransport}
                    isFilteredOffer={(offer) => isFilteredOffer(offer, filters)}
                    isFilteredSolution={(solution, classType) =>
                      isFilteredSolution(solution, filters, classType)
                    }
                    solution={selectedOffer.solution}
                    onConfirm={handleConfirmChoice}
                    cabinClass={selectedOfferClass as CabinClass}
                    onClassSelect={onClassSelect}
                    listVisible={listVisible}
                    isConfirmLoading={isConfirmLoading}
                  />
                )}
            </div>
            {hasSolutions && (
              <button
                type="button"
                disabled={isNextLoading}
                className="flex items-center gap-2 text-cnam-blue text-sm md:text-base font-bold font-jakarta mt-4 mb-6
                        "
                onClick={() => loadMore("next")}
              >
                {isNextLoading ? (
                  <Spinner $size={12} />
                ) : (
                  <Icon src="arrow_down_line" />
                )}
                {selectedTransport == Transportation.TRAIN
                  ? t("results.nextTrains")
                  : t("results.nextFlights")}
              </button>
            )}
          </div>
        </div>
      </div>

      <OfflineModal
        isOpen={isOfflineModalOpen}
        onClose={() => setOfflineModalOpen(false)}
        agencyInfo={agencyInfos}
        messageLabel="results.offlineText"
      />
    </>
  );
}
